/**
 * Atlas PM · 用户管理 · 数据 hooks（TanStack Query）
 *
 * 读：列表 / 组织树 / 角色列表。
 * 写：新建 / 编辑 / 启停 / 重置口令，成功后统一失效用户相关缓存。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  userApi,
  userKeys,
  type CreateUserInput,
  type UpdateUserInput,
  type UserListQuery,
} from './api';

export function useUserList(query: UserListQuery) {
  return useQuery({
    queryKey: userKeys.list(query),
    queryFn: () => userApi.list(query),
    // 翻页 / 搜索时保留上一页数据，避免表格闪空
    placeholderData: (prev) => prev,
  });
}

export function useOrgTree() {
  return useQuery({
    queryKey: userKeys.orgs(),
    queryFn: () => userApi.orgs(),
    staleTime: 5 * 60_000,
  });
}

export function useRoleList() {
  return useQuery({
    queryKey: userKeys.roles(),
    queryFn: () => userApi.roles(),
    staleTime: 5 * 60_000,
  });
}

/** 写操作后失效用户列表缓存（组织/角色为静态数据，无需失效） */
function useUserInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: userKeys.all });
}

export function useCreateUser() {
  const invalidate = useUserInvalidate();
  return useMutation({
    mutationFn: (input: CreateUserInput) => userApi.create(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateUser(id: string) {
  const invalidate = useUserInvalidate();
  return useMutation({
    mutationFn: (input: UpdateUserInput) => userApi.update(id, input),
    onSuccess: () => invalidate(),
  });
}

export function useSetUserStatus() {
  const invalidate = useUserInvalidate();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: number }) =>
      userApi.setStatus(id, status),
    onSuccess: () => invalidate(),
  });
}

export function useResetUserPassword() {
  const invalidate = useUserInvalidate();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      userApi.resetPassword(id, password),
    onSuccess: () => invalidate(),
  });
}
